import { useEffect, useState } from 'react';

// The deals board.
//
// It reads a list the scanner filled in earlier and never touches Smiles itself - so it loads
// instantly, and no visitor can cause a search. That also means every row is a snapshot: award
// seats disappear without warning, which is why each one says WHEN it was checked and why the
// action is "ask us", not "book now". Promising a seat we last saw six hours ago would be a
// promise we cannot keep.

const API = 'https://ym-travel-api.zalman.workers.dev/deals';
const WHATSAPP = '15551234567';   // replace with the real business number

type Deal = {
  id: number; origin: string; destination: string; dep_date: string;
  airline: string | null; cabin: string | null; stops: number | null;
  dep_time: string | null; arr_time: string | null;
  miles: number | null; seats: number | null;
  price_usd: number | null; cash_usd: number | null;
  checked_at: string;
};

const CITY: Record<string, string> = {
  LGA: 'New York', JFK: 'New York', EWR: 'Newark', NYC: 'New York',
  YYZ: 'Toronto', YTZ: 'Toronto', YUL: 'Montreal',
  ORD: 'Chicago', MIA: 'Miami', LAX: 'Los Angeles',
  SXM: 'St Maarten', AUA: 'Aruba', PUJ: 'Punta Cana', MBJ: 'Montego Bay',
  CUN: 'Cancun', NAS: 'Nassau', BGI: 'Barbados', FCO: 'Rome', ATH: 'Athens',
  CDG: 'Paris', BCN: 'Barcelona', LIS: 'Lisbon', MAD: 'Madrid', LHR: 'London',
};
const city = (c: string) => CITY[c] || c;

const ampm = (t: string | null) => {
  const m = String(t || '').match(/(\d{1,2})h(\d{2})/);
  if (!m) return t || '';
  let h = +m[1];
  const ap = h < 12 ? 'am' : 'pm';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ap}`;
};

const niceDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

// "checked 3 hours ago" - the honest part of the page.
function ago(iso: string) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return mins <= 1 ? 'just now' : `${mins} minutes ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

const isBusiness = (c: string | null) => /business|ejecutiv/i.test(c || '');

export default function Deals() {
  const [deals, setDeals] = useState<Deal[] | null>(null);
  const [err, setErr] = useState('');
  const [cabin, setCabin] = useState<'all' | 'economy' | 'business'>('all');
  const [nonstopOnly, setNonstopOnly] = useState(false);

  useEffect(() => {
    fetch(API)
      .then((r) => r.json())
      .then((d) => setDeals(d.deals || []))
      .catch((e) => setErr(String(e.message || e)));
  }, []);

  const shown = (deals || []).filter((d) => {
    if (cabin === 'business' && !isBusiness(d.cabin)) return false;
    if (cabin === 'economy' && isBusiness(d.cabin)) return false;
    if (nonstopOnly && (d.stops ?? 1) !== 0) return false;
    return true;
  });

  const ask = (d: Deal) => {
    const text = `Hi — I saw this on your deals page:\n` +
      `${city(d.origin)} (${d.origin}) to ${city(d.destination)} (${d.destination})\n` +
      `${niceDate(d.dep_date)}${d.dep_time ? ' at ' + ampm(d.dep_time) : ''}\n` +
      `${isBusiness(d.cabin) ? 'Business' : 'Economy'}${d.airline ? ' · ' + d.airline : ''}\n` +
      `Is it still available?`;
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="dealsWrap">
      <header className="dealsTop">
        <span className="dealsBrand">YM <span>Travels</span></span>
        <span className="dealsTag">Seats we are seeing right now</span>
      </header>

      <main className="dealsMain">
        <div className="dealsHead">
          <h1>Current finds</h1>
          <p>
            Real award seats, checked regularly. They come and go quickly — tell us which one you
            want and we will confirm it is still there.
          </p>
        </div>

        <div className="dealsFilters">
          {(['all', 'economy', 'business'] as const).map((c) => (
            <button key={c} className={'dealsChip' + (cabin === c ? ' on' : '')} onClick={() => setCabin(c)}>
              {c === 'all' ? 'All cabins' : c === 'economy' ? 'Economy' : 'Business'}
            </button>
          ))}
          <button className={'dealsChip' + (nonstopOnly ? ' on' : '')} onClick={() => setNonstopOnly(!nonstopOnly)}>
            Nonstop only
          </button>
        </div>

        {err && <div className="dealsEmpty">Could not load right now. Please try again shortly.</div>}

        {!err && deals === null && <div className="dealsEmpty">Looking…</div>}

        {!err && deals !== null && shown.length === 0 && (
          <div className="dealsEmpty">
            Nothing matching that at the moment. Message us with where you would like to go and we
            will look for you.
          </div>
        )}

        <div className="dealsList">
          {shown.map((d) => (
            <article key={d.id} className="dealCard">
              <div className="dealMain">
                <div className="dealRoute">
                  {city(d.origin)} <span className="dealArrow">→</span> {city(d.destination)}
                </div>
                <div className="dealWhen">{niceDate(d.dep_date)}{d.dep_time ? ` · ${ampm(d.dep_time)}` : ''}</div>
                <div className="dealMeta">
                  {d.airline || 'Airline'}
                  <span className="dealDot">·</span>
                  {(d.stops ?? 0) === 0 ? 'Nonstop' : `${d.stops} stop${(d.stops ?? 0) > 1 ? 's' : ''}`}
                  <span className="dealDot">·</span>
                  {isBusiness(d.cabin) ? 'Business' : 'Economy'}
                </div>
              </div>

              <div className="dealSide">
                {d.price_usd != null
                  ? <div className="dealPrice">around ${Math.round(d.price_usd)}</div>
                  : <div className="dealPrice muted">ask us</div>}
                {d.seats != null && d.seats > 0 && (
                  <div className={'dealSeats' + (d.seats <= 2 ? ' few' : '')}>
                    {d.seats} seat{d.seats === 1 ? '' : 's'} left
                  </div>
                )}
                <button className="dealAsk" onClick={() => ask(d)}>Ask about this</button>
                <div className="dealChecked">checked {ago(d.checked_at)}</div>
              </div>
            </article>
          ))}
        </div>

        <p className="dealsFoot">
          Award availability changes without notice. Everything here is what we last saw, not a
          held seat — we confirm before anything is booked.
        </p>
      </main>
    </div>
  );
}
